import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  FormTextField,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { GetAppRolesDocument, useCreateAppRoleMutation } from '@user-management/graphql';
import { useNavigate } from '@user-management/router';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

enum CreateAppRoleError {
  ROLE_ALREADY_EXIST = 'ALREADY_EXISTS',
  OTHER = 'OTHER',
}

const CreateRoleSchema = z.object({
  name: z.string().trim().min(1, 'Please enter role name.'),
  description: z.string().optional(),
});
type CreateRoleFormInput = z.infer<typeof CreateRoleSchema>;

interface RoleCreationProps {
  open: boolean;
  onClose: () => void;
}

export default function RoleCreationModal(props: Readonly<RoleCreationProps>) {
  const { open: isOpen, onClose } = props;

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const formContext = useForm<CreateRoleFormInput>({
    defaultValues: {
      name: '',
      description: undefined,
    },
    resolver: zodResolver(CreateRoleSchema),
  });

  const { formState, setError, handleSubmit } = formContext;
  const { isSubmitting } = formState;

  const [createAppRole, { loading: isCreateUserLoading }] = useCreateAppRoleMutation({
    onCompleted: (data) => {
      if (data?.suCreateAppRole.success) {
        showSnackbar({ message: 'Created Successfully', variant: 'success' });
        const appRoleId = data.suCreateAppRole.newAppRole?.id ?? '';
        onClose();
        navigate('/roles-and-permissions/:appRoleId/details', { params: { appRoleId } });
      } else {
        handleCreateAppRoleError(data?.suCreateAppRole.message);
      }
    },
    onError: (error) => {
      handleCreateAppRoleError(error.message);
    },
    refetchQueries: [GetAppRolesDocument],
  });

  const handleCreateAppRoleError = (errorMessage: string | null) => {
    if (!errorMessage) {
      showSnackbar({
        message: 'The role could not be created. Please try again.',
        variant: 'error',
      });
      return;
    }
    const RoleExistError = new RegExp(CreateAppRoleError.ROLE_ALREADY_EXIST, 'i');
    if (RoleExistError.test(errorMessage)) {
      setError('name', { message: 'This Role name already exists.' });
    } else {
      showSnackbar({ message: errorMessage, variant: 'error' });
    }
  };

  const handleCreateRole = async (data: CreateRoleFormInput) => {
    const { name: appRoleName, description: appRoleDescription } = data;
    await createAppRole({
      variables: {
        data: { name: appRoleName.trim(), description: (appRoleDescription ?? '').trim() },
      },
    });
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='sm'
        slotProps={{
          paper: { component: Form, onSubmit: handleSubmit(handleCreateRole) },
        }}
      >
        <DialogTitle onClose={onClose}>Create Role</DialogTitle>
        <Divider />
        <DialogContent>
          <Flex
            sx={{
              flexDirection: 'column',
              gap: (theme) => theme.spacing(4),
              '& .MuiInputLabel-root': { pb: (theme) => theme.spacing(1) },
            }}
          >
            <FormTextField
              name='name'
              label='Role Name'
              disabled={isSubmitting}
              required
              rules={{
                validate: (value: string) => {
                  if (!value.trim()) {
                    return 'Please enter role name.';
                  }
                },
              }}
            />
            <FormTextField
              name='description'
              label='Role Description'
              multiline
              minRows={2}
              disabled={isSubmitting}
              placeholder='Enter Role Description'
            />
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions sx={(theme) => ({ gap: theme.spacing(2), pt: theme.spacing(5) })}>
          <Button
            color='secondary'
            disabled={isCreateUserLoading}
            variant='outlined'
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button type='submit' loading={isCreateUserLoading}>
            Create Role
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
